import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Input from '../../../components/ui/Input'
import TypeaheadSearch, { TypeaheadOption } from '../../../components/ui/TypeaheadSearch'
import Badge from '../../../components/ui/Badge'
import Table, { Pagination } from '../../../components/ui/Table'
import Modal from '../../../components/ui/Modal'
import api from '../../../lib/api'
import { formatPhone } from '../../../lib/formatters'
import type { Doctor, PaginatedResponse } from '../../../types'

export default function DoctorsListPage() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['doctors', page, search],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '10' })
      if (search) params.append('search', search)
      const { data } = await api.get<PaginatedResponse<Doctor>>(`/doctors?${params}`)
      return data
    },
  })

  // Transform API response (snake_case) to frontend (camelCase)
  const transformDoctor = (d: any): Doctor => ({
    ...d,
    displayId: d.code || d.displayId,
    clinicName: d.clinic_name || d.clinicName,
    gstNumber: d.gstin || d.gstNumber,
    isActive: d.is_active ?? d.isActive ?? true,
  })

  const columns = [
    {
      key: 'displayId',
      header: 'ID',
      render: (doctor: Doctor) => {
        const d = transformDoctor(doctor)
        return (
          <Link to={`/doctors/${d.id}`} className="text-primary-600 hover:text-primary-700 font-medium">
            {d.displayId}
          </Link>
        )
      },
    },
    {
      key: 'name',
      header: 'Doctor Name',
      render: (doctor: Doctor) => {
        const d = transformDoctor(doctor)
        return (
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{d.name}</p>
          </div>
        )
      },
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (doctor: Doctor) => {
        const d = transformDoctor(doctor)
        return (
          <div className="text-sm">
            <p>{formatPhone(d.phone)}</p>
            {d.email && <p className="text-gray-500 dark:text-gray-400">{d.email}</p>}
          </div>
        )
      },
    },
    {
      key: 'clinic',
      header: 'Clinic',
      render: (doctor: Doctor) => {
        const d = transformDoctor(doctor)
        // Get address - prefer shipping > billing > any
        const addresses = (doctor as any).addresses || []
        const shippingAddress = addresses.find((a: any) => a.label?.toLowerCase() === 'shipping')
        const billingAddress = addresses.find((a: any) => a.label?.toLowerCase() === 'billing')
        const primaryAddress = shippingAddress || billingAddress || addresses.find((a: any) => a.is_default) || addresses[0]
        
        // Build address line: city, state if available
        let addressLine = ''
        if (primaryAddress) {
          const parts = []
          if (primaryAddress.city) parts.push(primaryAddress.city)
          if (primaryAddress.state) parts.push(primaryAddress.state)
          addressLine = parts.join(', ')
        }
        
        return (
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{d.clinicName || '-'}</p>
            {addressLine && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{addressLine}</p>
            )}
          </div>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (doctor: Doctor) => {
        const d = transformDoctor(doctor)
        return (
          <Badge variant={d.isActive ? 'success' : 'gray'}>
            {d.isActive ? 'Active' : 'Inactive'}
          </Badge>
        )
      },
    },
    {
      key: 'actions',
      header: '',
      render: (doctor: Doctor) => (
        <div className="flex justify-end gap-2">
          <Link
            to={`/doctors/${doctor.id}`}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
          >
            <i className="fas fa-eye"></i>
          </Link>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Doctors / Clients</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage your doctors and clinic clients
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <i className="fas fa-plus mr-2"></i>
          Add Doctor
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <TypeaheadSearch
              placeholder="Search by name, clinic, phone..."
              value={search}
              onChange={(value) => { setSearch(value); setPage(1) }}
              onSelect={(option) => { 
                window.location.href = `/doctors/${option.id}`
              }}
              queryKey={['doctors', 'search']}
              queryFn={async () => {
                try {
                  const { data } = await api.get<PaginatedResponse<Doctor>>('/doctors?limit=100')
                  return (data.data || []).map((doctor: Doctor): TypeaheadOption => ({
                    id: doctor.id,
                    label: doctor.name,
                    sublabel: doctor.clinicName || doctor.phone,
                    icon: 'fa-user-md',
                  }))
                } catch {
                  return []
                }
              }}
              maxResults={8}
            />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        <Table
          columns={columns}
          data={data?.data ?? []}
          loading={isLoading}
          keyExtractor={(doctor) => doctor.id}
          emptyMessage="No doctors found"
          onRowClick={(doctor) => window.location.href = `/doctors/${doctor.id}`}
        />
        {data && data.totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={data.totalPages}
            onPageChange={setPage}
          />
        )}
      </Card>

      {/* Create Modal */}
      <CreateDoctorModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false)
          queryClient.invalidateQueries({ queryKey: ['doctors'] })
        }}
      />
    </div>
  )
}

interface CreateDoctorModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

function CreateDoctorModal({ isOpen, onClose, onSuccess }: CreateDoctorModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    clinicName: '',
    phone: '',
    email: '',
    // Shipping address
    shippingAddressLine1: '',
    shippingAddressLine2: '',
    shippingCity: '',
    shippingState: '',
    shippingPincode: '',
    // Billing address
    billingAddressLine1: '',
    billingAddressLine2: '',
    billingCity: '',
    billingState: '',
    billingPincode: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true)

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    
    if (!formData.name.trim()) {
      newErrors.name = 'Please enter the doctor\'s name'
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters'
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Please enter a phone number'
    } else if (!/^[6-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = 'Must be a valid 10-digit Indian mobile number (starting with 6-9)'
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (formData.shippingPincode && !/^\d{6}$/.test(formData.shippingPincode)) {
      newErrors.shippingPincode = 'Pincode must be exactly 6 digits'
    }

    if (!billingSameAsShipping && formData.billingPincode && !/^\d{6}$/.test(formData.billingPincode)) {
      newErrors.billingPincode = 'Pincode must be exactly 6 digits'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      // If billing same as shipping, copy shipping to billing
      const billingAddress = billingSameAsShipping ? {
        billing_address_line1: data.shippingAddressLine1.trim() || undefined,
        billing_address_line2: data.shippingAddressLine2.trim() || undefined,
        billing_city: data.shippingCity.trim() || undefined,
        billing_state: data.shippingState.trim() || undefined,
        billing_pincode: data.shippingPincode.trim() || undefined,
      } : {
        billing_address_line1: data.billingAddressLine1.trim() || undefined,
        billing_address_line2: data.billingAddressLine2.trim() || undefined,
        billing_city: data.billingCity.trim() || undefined,
        billing_state: data.billingState.trim() || undefined,
        billing_pincode: data.billingPincode.trim() || undefined,
      }
      
      return api.post('/doctors', {
        name: data.name.trim(),
        clinic_name: data.clinicName.trim() || undefined,
        phone: `+91${data.phone}`,
        email: data.email.trim() || undefined,
        // Shipping address (primary)
        address_line1: data.shippingAddressLine1.trim() || undefined,
        address_line2: data.shippingAddressLine2.trim() || undefined,
        city: data.shippingCity.trim() || undefined,
        state: data.shippingState.trim() || undefined,
        pincode: data.shippingPincode.trim() || undefined,
        // Billing address
        ...billingAddress,
        billing_same_as_shipping: billingSameAsShipping,
      })
    },
    onSuccess: () => {
      setFormData({
        name: '',
        clinicName: '',
        phone: '',
        email: '',
        shippingAddressLine1: '',
        shippingAddressLine2: '',
        shippingCity: '',
        shippingState: '',
        shippingPincode: '',
        billingAddressLine1: '',
        billingAddressLine2: '',
        billingCity: '',
        billingState: '',
        billingPincode: '',
      })
      setErrors({})
      setBillingSameAsShipping(true)
      onSuccess()
    },
    onError: () => {
      setErrors({ form: 'Something went wrong. Please check your details and try again.' })
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validateForm()) {
      mutation.mutate(formData)
    }
  }

  const handleClose = () => {
    setFormData({
      name: '',
      clinicName: '',
      phone: '',
      email: '',
      shippingAddressLine1: '',
      shippingAddressLine2: '',
      shippingCity: '',
      shippingState: '',
      shippingPincode: '',
      billingAddressLine1: '',
      billingAddressLine2: '',
      billingCity: '',
      billingState: '',
      billingPincode: '',
    })
    setErrors({})
    setBillingSameAsShipping(true)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New Doctor" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Required Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Input
              label="Doctor Name *"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value })
                if (errors.name) setErrors({ ...errors, name: '' })
              }}
              error={errors.name}
              placeholder="Dr. John Doe"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Phone Number *
            </label>
            <div className="flex w-full">
              <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-l-lg text-sm whitespace-nowrap">
                +91
              </span>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => {
                  setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })
                  if (errors.phone) setErrors({ ...errors, phone: '' })
                }}
                className={`w-full min-w-0 px-4 py-2.5 border rounded-r-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.phone 
                    ? 'border-red-500 dark:border-red-500' 
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="9876543210"
              />
            </div>
            {errors.phone && (
              <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                <i className="fas fa-exclamation-circle text-xs"></i>
                {errors.phone}
              </p>
            )}
          </div>
        </div>

        {/* Optional Contact Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Clinic Name"
            value={formData.clinicName}
            onChange={(e) => setFormData({ ...formData, clinicName: e.target.value })}
            placeholder="Smile Dental Clinic"
          />
          <div>
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value })
                if (errors.email) setErrors({ ...errors, email: '' })
              }}
              error={errors.email}
              placeholder="doctor@clinic.com"
            />
          </div>
        </div>

        {/* Shipping Address Section - Always Visible */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex items-center gap-2 mb-4">
            <i className="fas fa-truck text-gray-400"></i>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Shipping Address (Optional)</span>
          </div>
          
          <div className="space-y-4">
            <Input
              label="Address Line 1"
              value={formData.shippingAddressLine1}
              onChange={(e) => setFormData({ ...formData, shippingAddressLine1: e.target.value })}
              placeholder="123, Main Street"
            />
            <Input
              label="Address Line 2"
              value={formData.shippingAddressLine2}
              onChange={(e) => setFormData({ ...formData, shippingAddressLine2: e.target.value })}
              placeholder="Near Central Park"
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input
                label="City"
                value={formData.shippingCity}
                onChange={(e) => setFormData({ ...formData, shippingCity: e.target.value })}
                placeholder="Mumbai"
              />
              <Input
                label="State"
                value={formData.shippingState}
                onChange={(e) => setFormData({ ...formData, shippingState: e.target.value })}
                placeholder="Maharashtra"
              />
              <Input
                label="Pincode"
                value={formData.shippingPincode}
                onChange={(e) => {
                  setFormData({ ...formData, shippingPincode: e.target.value.replace(/\D/g, '').slice(0, 6) })
                  if (errors.shippingPincode) setErrors({ ...errors, shippingPincode: '' })
                }}
                error={errors.shippingPincode}
                placeholder="400001"
              />
            </div>
          </div>
        </div>

        {/* Billing Address Section */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <i className="fas fa-file-invoice text-gray-400"></i>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Billing Address</span>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={billingSameAsShipping}
                onChange={(e) => setBillingSameAsShipping(e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 border-gray-300 dark:border-gray-600"
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">Same as shipping</span>
            </label>
          </div>
          
          {!billingSameAsShipping && (
            <div className="space-y-4">
              <Input
                label="Address Line 1"
                value={formData.billingAddressLine1}
                onChange={(e) => setFormData({ ...formData, billingAddressLine1: e.target.value })}
                placeholder="123, Main Street"
              />
              <Input
                label="Address Line 2"
                value={formData.billingAddressLine2}
                onChange={(e) => setFormData({ ...formData, billingAddressLine2: e.target.value })}
                placeholder="Near Central Park"
              />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Input
                  label="City"
                  value={formData.billingCity}
                  onChange={(e) => setFormData({ ...formData, billingCity: e.target.value })}
                  placeholder="Mumbai"
                />
                <Input
                  label="State"
                  value={formData.billingState}
                  onChange={(e) => setFormData({ ...formData, billingState: e.target.value })}
                  placeholder="Maharashtra"
                />
                <Input
                  label="Pincode"
                  value={formData.billingPincode}
                  onChange={(e) => {
                    setFormData({ ...formData, billingPincode: e.target.value.replace(/\D/g, '').slice(0, 6) })
                    if (errors.billingPincode) setErrors({ ...errors, billingPincode: '' })
                  }}
                  error={errors.billingPincode}
                  placeholder="400001"
                />
              </div>
            </div>
          )}
        </div>

        {/* Form-level error */}
        {(errors.form || mutation.isError) && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
            <i className="fas fa-exclamation-triangle text-red-500"></i>
            <p className="text-sm text-red-600 dark:text-red-400">
              {errors.form || 'Something went wrong. Please check your details and try again.'}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            <i className="fas fa-plus mr-2"></i>
            Add Doctor
          </Button>
        </div>
      </form>
    </Modal>
  )
}
